import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';

const NAV_SHORTCUTS = [
  { id: 'command',      label: 'Command Center',   section: 'Analytics',  keys: 'G O' },
  { id: 'revenuehub',   label: 'Revenue',          section: 'Analytics',  keys: 'G R' },
  { id: 'users',        label: 'Users',            section: 'Analytics',  keys: 'G U' },
  { id: 'retention',    label: 'Retention',        section: 'Analytics',  keys: null  },
  { id: 'directory',    label: 'Directory',        section: 'Analytics',  keys: 'G D' },
  { id: 'sana',         label: 'Sana',             section: 'Analytics',  keys: null  },
  { id: 'system',       label: 'System',           section: 'Analytics',  keys: 'G S' },
  { id: 'locations',    label: 'Locations',        section: 'Manage',     keys: null  },
  { id: 'passmanager',  label: 'Members',          section: 'Manage',     keys: null  },
  { id: 'partners',     label: 'Partners',         section: 'Manage',     keys: null  },
  { id: 'customerhelp', label: 'Customer Help',    section: 'Team',       keys: 'G H' },
  { id: 'planner',      label: 'Planner',          section: 'Team',       keys: null  },
  { id: 'roles',        label: 'Roles & Access',   section: 'Team',       keys: null  },
  { id: 'campaigns',    label: 'Campaigns',        section: 'Marketing',  keys: null  },
  { id: 'waitlist',     label: 'Waitlist',         section: 'Marketing',  keys: 'G W' },
  { id: 'brandkit',     label: 'Brand Kit',        section: 'Marketing',  keys: null  },
]

function Highlight({ text, query }) {
  if (!query) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: '#AFE1F6', color: '#0f1e28', borderRadius: 2, padding: '0 1px' }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

export default function CmdK({ onNav }) {
  const [open, setOpen]       = useState(false);
  const [query, setQuery]     = useState('');
  const [cursor, setCursor]   = useState(0);
  const [locations, setLocs]  = useState([]);
  const [tickets, setTickets] = useState([]);
  const [locLoading, setLL]   = useState(false);
  const inputRef   = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    function handler(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(o => !o);
        setQuery('');
        setCursor(0);
      }
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setLocs([]); setTickets([]); return; }
    setLL(true);
    try {
      const [locRes, tixRes] = await Promise.all([
        supabase.rpc('admin_search_locations', { p_query: q, p_limit: 5 }),
        supabase.rpc('admin_get_tickets'),
      ]);
      setLocs(locRes.data || []);
      const filtered = (tixRes.data || []).filter(t =>
        t.subject?.toLowerCase().includes(q.toLowerCase()) ||
        t.email?.toLowerCase().includes(q.toLowerCase())
      ).slice(0, 3);
      setTickets(filtered);
    } catch (_e) {}
    setLL(false);
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 220);
  }, [query, search]);

  const navMatches = query
    ? NAV_SHORTCUTS.filter(n => n.label.toLowerCase().includes(query.toLowerCase()))
    : NAV_SHORTCUTS.slice(0, 6);

  const allResults = [
    ...navMatches.map(n => ({ type: 'nav', ...n })),
    ...tickets.map(t => ({ type: 'ticket', id: 'customerhelp', label: t.subject, sub: t.email, status: t.status })),
    ...locations.map(l => ({ type: 'location', id: 'locationprofile', label: l.name, sub: [l.city, l.country].filter(Boolean).join(', '), locationId: l.id })),
  ];

  useEffect(() => { setCursor(0); }, [query]);

  function select(result) {
    if (result.id) onNav(result.id);
    setOpen(false);
    setQuery('');
  }

  function handleKey(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, allResults.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    if (e.key === 'Enter' && allResults[cursor]) select(allResults[cursor]);
  }

  const statusBg  = { open: '#fff8e1', in_progress: '#e3f0ff', resolved: '#e8f5e9' };
  const statusClr = { open: '#7d4e00', in_progress: '#1a4a8a', resolved: '#1b5e20' };

  if (!open) return null;

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(10,22,32,0.62)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '13vh', fontFamily: F }}
      onMouseDown={() => setOpen(false)}
    >
      <div style={{ width: 580, background: '#fff', borderRadius: 14, boxShadow: '0 24px 64px rgba(0,0,0,0.28)', overflow: 'hidden', maxHeight: '65vh', display: 'flex', flexDirection: 'column' }}
        onMouseDown={e => e.stopPropagation()}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid #edf0f3' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9eb8c8" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={handleKey}
            placeholder="Search screens, locations, tickets…"
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: '.9rem', color: '#0f1e28', fontFamily: F, background: 'transparent' }} />
          {locLoading && (
            <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid #AFE1F6', borderTopColor: '#314F5C', animation: 'cmdk-spin .6s linear infinite' }} />
          )}
          <kbd style={{ fontSize: '.62rem', background: '#f0f2f4', color: '#7a9aaa', padding: '2px 6px', borderRadius: 4, border: '1px solid #e0e6ea' }}>ESC</kbd>
        </div>

        <style>{`@keyframes cmdk-spin { to { transform: rotate(360deg); } }`}</style>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {allResults.length === 0 && query.length > 1 && !locLoading && (
            <div style={{ padding: '24px 0', textAlign: 'center', color: '#a0b4be', fontSize: '.82rem' }}>No results for "{query}"</div>
          )}

          {navMatches.length > 0 && (
            <>
              <div style={{ padding: '8px 16px 3px', fontSize: '.58rem', fontWeight: 700, color: '#9eb8c8', letterSpacing: '.08em', textTransform: 'uppercase' }}>
                {query ? 'Screens' : 'Quick navigation'}
              </div>
              {navMatches.map((n, i) => {
                const active = cursor === i;
                return (
                  <div key={n.id + '_' + i} onMouseEnter={() => setCursor(i)} onClick={() => select({ ...n, type: 'nav' })}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', cursor: 'pointer', background: active ? '#edf4f8' : 'transparent', transition: 'background .08s' }}>
                    <div style={{ width: 28, height: 28, borderRadius: 7, background: active ? '#314F5C' : '#f0f4f7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background .08s' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={active ? '#AFE1F6' : '#6F8FA7'} strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '.82rem', fontWeight: 500, color: '#0f1e28' }}><Highlight text={n.label} query={query} /></div>
                      <div style={{ fontSize: '.6rem', color: '#9eb8c8' }}>{n.section}</div>
                    </div>
                    {n.keys && <kbd style={{ fontSize: '.58rem', background: '#f0f2f4', color: '#7a9aaa', padding: '2px 6px', borderRadius: 4, border: '1px solid #e0e6ea', letterSpacing: '.04em' }}>{n.keys}</kbd>}
                  </div>
                );
              })}
            </>
          )}

          {tickets.length > 0 && (
            <>
              <div style={{ padding: '8px 16px 3px', fontSize: '.58rem', fontWeight: 700, color: '#9eb8c8', letterSpacing: '.08em', textTransform: 'uppercase' }}>Support tickets</div>
              {tickets.map((t, i) => {
                const ri = navMatches.length + i;
                const active = cursor === ri;
                return (
                  <div key={i} onMouseEnter={() => setCursor(ri)} onClick={() => select(t)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', cursor: 'pointer', background: active ? '#edf4f8' : 'transparent' }}>
                    <div style={{ width: 28, height: 28, borderRadius: 7, background: active ? '#314F5C' : '#f0f4f7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={active ? '#AFE1F6' : '#6F8FA7'} strokeWidth="2" strokeLinecap="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '.82rem', fontWeight: 500, color: '#0f1e28', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><Highlight text={t.label} query={query} /></div>
                      <div style={{ fontSize: '.6rem', color: '#9eb8c8' }}>{t.sub}</div>
                    </div>
                    <span style={{ fontSize: '.58rem', fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: statusBg[t.status] || '#f0f2f4', color: statusClr[t.status] || '#7a9aaa', flexShrink: 0 }}>
                      {(t.status || '').replace('_', ' ')}
                    </span>
                  </div>
                );
              })}
            </>
          )}

          {locations.length > 0 && (
            <>
              <div style={{ padding: '8px 16px 3px', fontSize: '.58rem', fontWeight: 700, color: '#9eb8c8', letterSpacing: '.08em', textTransform: 'uppercase' }}>Locations</div>
              {locations.map((l, i) => {
                const ri = navMatches.length + tickets.length + i;
                const active = cursor === ri;
                return (
                  <div key={i} onMouseEnter={() => setCursor(ri)} onClick={() => select(l)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', cursor: 'pointer', background: active ? '#edf4f8' : 'transparent' }}>
                    <div style={{ width: 28, height: 28, borderRadius: 7, background: active ? '#314F5C' : '#f0f4f7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={active ? '#AFE1F6' : '#6F8FA7'} strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '.82rem', fontWeight: 500, color: '#0f1e28', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><Highlight text={l.label} query={query} /></div>
                      <div style={{ fontSize: '.6rem', color: '#9eb8c8' }}>{l.sub}</div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div style={{ padding: '7px 16px', borderTop: '1px solid #f0f2f4', display: 'flex', gap: 12, alignItems: 'center' }}>
          {[['↑↓', 'navigate'], ['↵', 'open'], ['esc', 'close'], ['⌘K', 'toggle']].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <kbd style={{ fontSize: '.58rem', background: '#f0f2f4', color: '#7a9aaa', padding: '2px 5px', borderRadius: 4, border: '1px solid #e0e6ea' }}>{k}</kbd>
              <span style={{ fontSize: '.58rem', color: '#a0b4be' }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
